import React, { useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// PWAManager — handles both "Install App" prompt and "New Version" banner
// Usage: <PWAManager /> anywhere inside App (renders nothing until needed)
// ─────────────────────────────────────────────────────────────────────────────

export default function PWAManager() {
  const [installPrompt, setInstallPrompt] = useState(null);  // beforeinstallprompt event
  const [showInstall, setShowInstall] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [skipWaiting, setSkipWaiting] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  // ── Capture install prompt ──────────────────────────────────────────────────
  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // Don't show if user dismissed this session
    if (sessionStorage.getItem('pwa-install-dismissed')) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Small delay so user settles in before we prompt
      setTimeout(() => setShowInstall(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // ── Listen for SW update event ─────────────────────────────────────────────
  useEffect(() => {
    const handleUpdate = (e) => {
      setSkipWaiting(() => e.detail?.skipWaiting);
      setShowUpdate(true);
    };
    window.addEventListener('sw:updated', handleUpdate);
    return () => window.removeEventListener('sw:updated', handleUpdate);
  }, []);

  // ── Install handlers ───────────────────────────────────────────────────────
  const handleInstall = useCallback(async () => {
    if (!installPrompt) return;
    const { outcome } = await installPrompt.prompt();
    console.log('[PWA] Install outcome:', outcome);
    setInstallPrompt(null);
    setShowInstall(false);
  }, [installPrompt]);

  const handleDismissInstall = () => {
    setDismissed(true);
    setShowInstall(false);
    sessionStorage.setItem('pwa-install-dismissed', '1');
  };

  // ── Update handlers ────────────────────────────────────────────────────────
  const handleUpdate = () => {
    if (skipWaiting) skipWaiting();
    setShowUpdate(false);
  };

  const handleDismissUpdate = () => {
    setUpdateDismissed(true);
    setShowUpdate(false);
  };

  if (dismissed && updateDismissed) return null;

  return (
    <>
      {/* ── Install Banner ──────────────────────────────────────────────── */}
      {showInstall && !dismissed && (
        <div style={styles.banner}>
          <div style={styles.bannerLeft}>
            <span style={styles.bannerIcon}>🎮</span>
            <div>
              <div style={styles.bannerTitle}>ติดตั้ง Fastlane LV</div>
              <div style={styles.bannerSubtitle}>เล่นเกมได้ทุกที่ แม้ไม่มีอินเทอร์เน็ต</div>
            </div>
          </div>
          <div style={styles.bannerActions}>
            <button style={styles.btnInstall} onClick={handleInstall}>
              ติดตั้ง
            </button>
            <button style={styles.btnClose} onClick={handleDismissInstall} aria-label="ปิด">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Update Banner ───────────────────────────────────────────────── */}
      {showUpdate && !updateDismissed && (
        <div style={{ ...styles.banner, ...styles.updateBanner }}>
          <div style={styles.bannerLeft}>
            <span style={styles.bannerIcon}>🚀</span>
            <div>
              <div style={styles.bannerTitle}>มีเวอร์ชันใหม่พร้อมใช้!</div>
              <div style={styles.bannerSubtitle}>รีโหลดเพื่ออัปเดตแอปเป็นเวอร์ชันล่าสุด</div>
            </div>
          </div>
          <div style={styles.bannerActions}>
            <button style={styles.btnUpdate} onClick={handleUpdate}>
              อัปเดตเลย
            </button>
            <button style={styles.btnClose} onClick={handleDismissUpdate} aria-label="ปิด">
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  banner: {
    position: 'fixed',
    bottom: '80px',          // above any bottom nav / chat widget
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 32px)',
    maxWidth: '480px',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    border: '1px solid rgba(0,255,136,0.25)',
    borderRadius: '16px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,255,136,0.1)',
    zIndex: 9000,
    animation: 'pwaSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    backdropFilter: 'blur(12px)',
  },
  updateBanner: {
    border: '1px solid rgba(99,102,241,0.35)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1)',
  },
  bannerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  bannerIcon: {
    fontSize: '32px',
    flexShrink: 0,
  },
  bannerTitle: {
    color: '#f1f5f9',
    fontWeight: '700',
    fontSize: '0.9rem',
    marginBottom: '2px',
  },
  bannerSubtitle: {
    color: '#64748b',
    fontSize: '0.78rem',
    lineHeight: 1.4,
  },
  bannerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  btnInstall: {
    background: 'linear-gradient(135deg, #00ff88, #00cc6a)',
    color: '#0a0a0a',
    border: 'none',
    borderRadius: '10px',
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  btnUpdate: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  btnClose: {
    background: 'transparent',
    border: '1px solid #1e293b',
    borderRadius: '8px',
    color: '#475569',
    padding: '6px 10px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    lineHeight: 1,
    transition: 'background 0.15s, color 0.15s',
  },
};

// Inject keyframe once
if (typeof document !== 'undefined' && !document.getElementById('pwa-keyframes')) {
  const style = document.createElement('style');
  style.id = 'pwa-keyframes';
  style.textContent = `
    @keyframes pwaSlideUp {
      from { opacity: 0; transform: translateX(-50%) translateY(24px); }
      to   { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
  `;
  document.head.appendChild(style);
}
